import { Outlet, Navigate, useLocation } from "react-router-dom";
import { Sidebar } from "@/components/dashboard/sidebar";
import { Header } from "@/components/dashboard/header";
import { Topbar } from "@/components/dashboard/Topbar";
import { CommandMenu } from "@/components/dashboard/CommandMenu";
import { useCommandMenuHotkey } from "@/components/dashboard/use-command-menu";
import { Suspense, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { FullPageLoader } from "@/components/ui/full-page-loader";
import { AdminPageSkeleton } from "@/components/admin/AdminPageSkeleton";
import { easeOut } from "@/components/landing/marketing-motion";
import { dashboardStyles } from "@/components/dashboard/shared/dashboard-styles";
import { cn } from "@/lib/utils";

export function DashboardLayout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { user, isLoading, currentStore, stores, isLoadingStores } = useAuth();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);

  useCommandMenuHotkey(setIsSearchOpen);

  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (isLoading || isLoadingStores) {
    return <FullPageLoader text="Loading your dashboard" />;
  }

  if (user?.onboardingStatus === "pending") {
    return <Navigate to="/onboarding" replace />;
  }

  if (stores.length === 0) {
    return <Navigate to="/onboarding" replace />;
  }

  if (!currentStore) {
    return <FullPageLoader text="Opening your store" />;
  }

  const openSearch = () => setIsSearchOpen(true);

  return (
    <div className={cn("min-h-screen", dashboardStyles.canvas)}>
      <Header
        onMobileMenuClick={() => setIsMobileMenuOpen(true)}
        onSearchClick={openSearch}
      />
      <div className="flex h-[calc(100dvh-3.5rem)] md:h-dvh md:p-2 md:pl-0">
        <Sidebar
          isOpen={isMobileMenuOpen}
          onOpenChange={setIsMobileMenuOpen}
          onOpenSearch={openSearch}
        />
        <main
          ref={mainRef}
          className={cn(
            "flex-1 overflow-auto p-4 pb-10 md:p-8 md:pb-12",
            dashboardStyles.mainPanel,
          )}
        >
          <Topbar />
          <div className="mx-auto max-w-[1440px]">
            <Suspense fallback={<AdminPageSkeleton />}>
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18, ease: easeOut }}
              >
                <Outlet />
              </motion.div>
            </Suspense>
          </div>
        </main>
      </div>
      <CommandMenu open={isSearchOpen} onOpenChange={setIsSearchOpen} />
    </div>
  );
}
